import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MachineSpec } from '@/types/flexe';
import { computeKW } from '@/lib/flexeKWConversion';

interface MachineSpecInputProps {
  label: string;
  spec: MachineSpec;
  onChange: (spec: MachineSpec) => void;
}

export function MachineSpecInput({ label, spec, onChange }: MachineSpecInputProps) {
  const updateSpec = (updates: Partial<MachineSpec>) => {
    onChange({ ...spec, ...updates });
  };

  // Calculate kW preview if using amps
  let calculatedKW: number | null = null;
  if (spec.amps && spec.amps > 0) {
    try {
      calculatedKW = computeKW(spec);
    } catch (e) {
      // Invalid spec, don't show preview
    }
  }

  return (
    <div className="space-y-3 p-3 border rounded-lg bg-muted/20">
      <Label className="text-sm font-medium">{label}</Label>

      {/* Power Input Group */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-xs">Vermogen (kW)</Label>
          <Input
            type="number"
            step="0.01"
            placeholder="bijv. 15.0"
            value={spec.kw || ''}
            onChange={(e) => updateSpec({ kw: e.target.value ? Number(e.target.value) : undefined })}
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Of Ampère (A)</Label>
          <Input
            type="number"
            step="0.1"
            placeholder="bijv. 25"
            value={spec.amps || ''}
            onChange={(e) => updateSpec({ amps: e.target.value ? Number(e.target.value) : undefined })}
          />
        </div>
      </div>

      {/* Elektrotechnische Details (alleen als Ampère gebruikt) */}
      {spec.amps && (
        <div className="space-y-3 p-2 border rounded-lg bg-background/50">
          <Label className="text-xs font-medium">Elektrotechnische Specs</Label>

          {/* Phases */}
          <div className="space-y-1">
            <Label className="text-xs">Fasen</Label>
            <Select
              value={spec.phases?.toString() || '3'}
              onValueChange={(v) => {
                const phases = Number(v) as 1 | 3;
                updateSpec({
                  phases,
                  voltage: phases === 3 ? 400 : 230, // Auto-adjust voltage
                });
              }}
            >
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1-fase (230V)</SelectItem>
                <SelectItem value="3">3-fase (400V)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Voltage */}
          <div className="space-y-1">
            <Label className="text-xs">Spanning (V)</Label>
            <Input
              type="number"
              className="h-8"
              value={spec.voltage || (spec.phases === 1 ? 230 : 400)}
              onChange={(e) => updateSpec({ voltage: Number(e.target.value) })}
            />
          </div>

          {/* Power Factor */}
          <div className="space-y-1">
            <Label className="text-xs">Power Factor (cosφ)</Label>
            <Input
              type="number"
              step="0.01"
              min="0.5"
              max="1.0"
              className="h-8"
              value={spec.pf ?? 0.9}
              onChange={(e) => updateSpec({ pf: Number(e.target.value) })}
            />
            <p className="text-xs text-muted-foreground">
              Typisch: 0.8-0.95 voor machines
            </p>
          </div>

          {/* Calculated kW preview */}
          {calculatedKW !== null && (
            <div className="pt-2 border-t">
              <p className="text-xs font-medium text-primary">
                Berekend vermogen: ~{calculatedKW.toFixed(1)} kW
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
